import { SavedCategory, SavedDashboard, UserLicenseRuleSet, RuleSet, AccessLevelRuleSet } from '../Services/Embeddedbi.types';

const US_REGION = 'us-west-2';
const AU_REGION = 'ap-southeast-2';
const EU_REGION = 'eu-west-1';

const CSV_HEADERS = [
  'Title',
  'Status',
  'Is Private',
  'Deployments',
  'Categories',
  'US QuickSight ID',
  'AU QuickSight ID',
  'EU QuickSight ID',
  'Description',
  'Created By',
  'Created Date',
  'Updated By',
  'Updated Date',
  'Licence Permissions',
  'Financial Access',
  'Access Level',
];

export function csvEscape(value: string | boolean | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim();
}

export function getRegionQuickSightId(dashboard: SavedDashboard, region: string): string {
  return (dashboard.dashboardRegionLinks ?? []).find(link => link.region === region)?.quicksightId ?? '';
}

export function formatLicencePermissions(ruleSets: UserLicenseRuleSet[]): string {
  const keys: (keyof Omit<UserLicenseRuleSet, 'id'>)[] = [
    'businessIntelligence', 'crm', 'projects', 'service', 'retainers', 'psa',
  ];
  return ruleSets
    .map(rs => keys.filter(key => rs[key]).join('; '))
    .filter(s => s.length > 0)
    .join(' | ');
}

export function formatFinancialAccess(ruleSets: RuleSet[]): string {
  const keys: (keyof RuleSet)[] = [
    'financialVisibility', 'costProfitJob', 'costProfitContract', 'costProfitGlobal', 'adminWithFinancialVisibility',
  ];
  return ruleSets
    .map(rs => keys.filter(key => rs[key]).join('; '))
    .filter(s => s.length > 0)
    .join(' | ');
}

export function formatAccessLevel(ruleSet: AccessLevelRuleSet | null | undefined): string {
  if (!ruleSet) return '';
  const keys: (keyof AccessLevelRuleSet)[] = ['admin', 'professional', 'contractor', 'collaborator'];
  return keys.filter(key => ruleSet[key]).join('; ');
}

export function buildReportsCsv(categories: SavedCategory[]): string {
  const dashboardMap = new Map<string, { dashboard: SavedDashboard; categoryTitles: string[] }>();

  for (const category of categories) {
    for (const dashboard of category.dashboards) {
      const existing = dashboardMap.get(dashboard.id);
      if (existing) {
        existing.categoryTitles.push(category.title);
      } else {
        dashboardMap.set(dashboard.id, { dashboard, categoryTitles: [category.title] });
      }
    }
  }

  const rows: string[] = [CSV_HEADERS.join(',')];

  for (const { dashboard, categoryTitles } of dashboardMap.values()) {
    const row = [
      csvEscape(dashboard.title),
      csvEscape(dashboard.status),
      csvEscape(dashboard.isPrivate),
      csvEscape((dashboard.deployments ?? []).join('; ')),
      csvEscape(categoryTitles.join('; ')),
      csvEscape(getRegionQuickSightId(dashboard, US_REGION)),
      csvEscape(getRegionQuickSightId(dashboard, AU_REGION)),
      csvEscape(getRegionQuickSightId(dashboard, EU_REGION)),
      csvEscape(stripHtml(dashboard.description)),
      csvEscape(dashboard.createdBy),
      csvEscape(dashboard.created),
      csvEscape(dashboard.lastUpdatedBy),
      csvEscape(dashboard.lastUpdated),
      csvEscape(formatLicencePermissions(dashboard.userLicenseRuleSets ?? [])),
      csvEscape(formatFinancialAccess(dashboard.ruleSets ?? [])),
      csvEscape(formatAccessLevel(dashboard.accessLevelRuleSet)),
    ];
    rows.push(row.join(','));
  }

  return rows.join('\r\n');
}

export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
